import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Button, Divider, Grid, RadioGroup, Stack, Typography } from "@mui/material";
import { Drawer } from "@zfp-akademie-a-s/zfp-design";
import { FormSelect } from "@zfp-akademie-a-s/zfp-design/form";
import { type DrawerIncomeFormType, getDrawerIncomeFormSchema } from "Forms/Schemas/Budget/Incomes/drawerIncomeFormSchema";
import { type Option, toSelectOptions } from "Forms/fieldOptions";
import { type AnalysisClientItem, IncomeIncomeType } from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { formatClientName, isPlannedChild } from "Infrastructure/Utils/ClientUtils";
import { type FC, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { DisabilityIncomeForm } from "../Forms/DisabilityIncomeForm";
import { EmploymentIncomeForm } from "../Forms/EmploymentIncomeForm";
import { OrphanOrWidowIncomeForm } from "../Forms/OrphanOrWidowIncomeForm";
import { OtherIncomeForm } from "../Forms/OtherIncomeForm";
import { ParentalIncomeForm } from "../Forms/ParentalIncomeForm";
import { PensionIncomeForm } from "../Forms/PensionIncomeForm";
import { RentalIncomeForm } from "../Forms/RentalIncomeForm";
import { SelfEmploymentIncomeForm } from "../Forms/SelfEmploymentIncomeForm";
import { IncomeTypeTile } from "./IncomeTypeTile";

type Props = {
  onClose: () => void;
  clients?: AnalysisClientItem[];
  readonly open: boolean;
};

export type SubmitIncomeFormHandle = {
  readonly submitForm: () => void;
};

export const AddIncomeDrawer: FC<Props> = ({ onClose, clients, open }) => {
  const { t } = useResource();
  const [incomeType, setIncomeType] = useState<IncomeIncomeType>(IncomeIncomeType.employment);

  const employmentIncomeFormRef = useRef<SubmitIncomeFormHandle>(null);
  const selfEmploymentIncomeFormRef = useRef<SubmitIncomeFormHandle>(null);
  const rentalIncomeFormRef = useRef<SubmitIncomeFormHandle>(null);
  const parentalIncomeFormRef = useRef<SubmitIncomeFormHandle>(null);
  const pensionIncomeFormRef = useRef<SubmitIncomeFormHandle>(null);
  const disabilityIncomeFormRef = useRef<SubmitIncomeFormHandle>(null);
  const orphanOrWidowIncomeFormRef = useRef<SubmitIncomeFormHandle>(null);
  const otherIncomeFormRef = useRef<SubmitIncomeFormHandle>(null);

  const clientOptions: Option[] = (clients ?? [])
    .filter((client) => !isPlannedChild(client))
    .map((client) => ({
      value: client.guid,
      name: formatClientName(client.firstName, client.surname, client.birthDate ?? null),
    }));

  const { control, watch, setValue } = useForm<DrawerIncomeFormType>({
    resolver: yupResolver(getDrawerIncomeFormSchema(t)),
    mode: "onBlur",
    defaultValues: {
      clientId: "",
    },
  });

  const selectedClientId = watch("clientId");

  const handleClose = () => {
    setValue("clientId", "");
    onClose();
  };

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      closeLabel={t(Resources.Common.Close)}
      title={t(Resources.AnalysisDetail.Budget.Incomes.Add)}
      primaryAction={
        <Button
          variant="contained"
          disabled={!selectedClientId}
          type="submit"
          onClick={() => {
            if (incomeType === IncomeIncomeType.employment) {
              employmentIncomeFormRef.current?.submitForm();
            }
            if (incomeType === IncomeIncomeType.self_employment) {
              selfEmploymentIncomeFormRef.current?.submitForm();
            }
            if (incomeType === IncomeIncomeType.rental) {
              rentalIncomeFormRef.current?.submitForm();
            }
            if (incomeType === IncomeIncomeType.parental) {
              parentalIncomeFormRef.current?.submitForm();
            }
            if (incomeType === IncomeIncomeType.pension) {
              pensionIncomeFormRef.current?.submitForm();
            }
            if (incomeType === IncomeIncomeType.disability) {
              disabilityIncomeFormRef.current?.submitForm();
            }
            if (incomeType === IncomeIncomeType.orphan_or_widow_pension) {
              orphanOrWidowIncomeFormRef.current?.submitForm();
            }
            if (incomeType === IncomeIncomeType.other) {
              otherIncomeFormRef.current?.submitForm();
            }
          }}
        >
          {t(Resources.Common.Create)}
        </Button>
      }
    >
      <Stack spacing={Spacing.XXL} mb={Spacing.XXL}>
        <FormSelect
          control={control}
          name="clientId"
          label={t(Resources.AnalysisDetail.Budget.Incomes.IncomeBelongsTo)}
          required
          options={toSelectOptions(clientOptions)}
          placeholder={t(Resources.Common.SelectPlaceholder)}
          fullWidth
        />
        <Divider />
      </Stack>

      <Box mb={Spacing.XXL}>
        <Stack spacing={Spacing.XL}>
          <Typography variant="subtitle1" fontWeight={600}>
            {t(Resources.AnalysisDetail.Budget.Incomes.IncomeType)}
          </Typography>

          <RadioGroup
            aria-label={t(Resources.AnalysisDetail.Budget.Incomes.IncomeType)}
            value={incomeType}
            onChange={(_, value) => {
              setIncomeType(value as IncomeIncomeType);
            }}
            sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: Spacing.M }}
          >
            {(Object.values(IncomeIncomeType) as Array<IncomeIncomeType>).map((value) => (
              <IncomeTypeTile key={value} incomeType={value} />
            ))}
          </RadioGroup>
        </Stack>
      </Box>

      <Box mb={Spacing.XXL}>
        {incomeType === IncomeIncomeType.employment && (
          <EmploymentIncomeForm onClose={handleClose} clientId={selectedClientId} ref={employmentIncomeFormRef} />
        )}
        {incomeType === IncomeIncomeType.self_employment && (
          <SelfEmploymentIncomeForm onClose={handleClose} clientId={selectedClientId} ref={selfEmploymentIncomeFormRef} />
        )}
        {incomeType === IncomeIncomeType.rental && (
          <RentalIncomeForm onClose={handleClose} clientId={selectedClientId} ref={rentalIncomeFormRef} />
        )}
        {incomeType === IncomeIncomeType.parental && (
          <ParentalIncomeForm onClose={handleClose} clientId={selectedClientId} ref={parentalIncomeFormRef} />
        )}
        {incomeType === IncomeIncomeType.pension && (
          <PensionIncomeForm onClose={handleClose} clientId={selectedClientId} ref={pensionIncomeFormRef} />
        )}
        {incomeType === IncomeIncomeType.disability && (
          <DisabilityIncomeForm onClose={handleClose} clientId={selectedClientId} ref={disabilityIncomeFormRef} />
        )}
        {incomeType === IncomeIncomeType.orphan_or_widow_pension && (
          <OrphanOrWidowIncomeForm onClose={handleClose} clientId={selectedClientId} ref={orphanOrWidowIncomeFormRef} />
        )}
        {incomeType === IncomeIncomeType.other && (
          <OtherIncomeForm onClose={handleClose} clientId={selectedClientId} ref={otherIncomeFormRef} />
        )}
      </Box>
    </Drawer>
  );
};
